import L from "leaflet";
import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AttributionControl,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { Link } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { makeMapKey, useInspectionsStore } from "../../stores/inspections";
import { useNominatimStore } from "../../stores/nominatim";
import { useUserStore } from "../../stores/users";
import "../../scss/inspections-map.scss";

// Munich city center — matches the typical business catchment area.
const DEFAULT_CENTER = [48.1351, 11.582];
const DEFAULT_ZOOM = 9;
const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Distinct, high-contrast palette for device-type markers. Colors are
// spaced around the color wheel so any two adjacent slots stay visually
// separable on both light and dark OSM tiles.
const DEVICE_COLOR_PALETTE = [
  "#1f77b4", // blue
  "#ff7f0e", // orange
  "#2ca02c", // green
  "#d62728", // red
  "#9467bd", // purple
  "#8c564b", // brown
  "#e377c2", // pink
  "#bcbd22", // olive
  "#17becf", // cyan
  "#ffd700", // gold
  "#000075", // navy
  "#006d77", // deep teal
];
const DEVICE_COLOR_FALLBACK = "#6c757d"; // Bootstrap secondary — "unknown"

// Module-level cache keeps each device_type's color stable for the whole
// session. Types are registered in alphabetical order and get the next
// free palette slot, which guarantees no two types share a color until
// the palette is exhausted.
const deviceColorCache = new Map();

// Sentinel used to represent "items without a device_type" inside the
// legend-filter state (since `null` already means "show all").
export const UNKNOWN_TYPE = "__unknown__";

// Build a Leaflet divIcon for a given hex color. The SVG reproduces the
// classic Leaflet teardrop pin (25x41) so we keep the familiar shape but
// recolor the fill per device type. Memoized per color so Leaflet isn't
// rebuilding the same icon on every render.
const iconCache = new Map();
const getColoredIcon = (color) => {
  if (iconCache.has(color)) return iconCache.get(color);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41" ` +
    `style="filter: drop-shadow(0 2px 2px rgba(0,0,0,0.4));">` +
    `<path d="M12.5 0.5C5.873 0.5 0.5 5.873 0.5 12.5c0 8.5 12 28 12 28s12-19.5 12-28` +
    `C24.5 5.873 19.127 0.5 12.5 0.5z" fill="${color}" stroke="#fff" stroke-width="1.2"/>` +
    `<circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/>` +
    `</svg>`;
  const icon = L.divIcon({
    className: "",
    html: svg,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -36],
  });
  iconCache.set(color, icon);
  return icon;
};

// When the map is first mounted inside a hidden Bootstrap tab-pane, Leaflet
// measures the container at 0x0 and caches the resulting (wrong) tile grid,
// which shows up as gray patches once the tab becomes visible. Re-invalidate
// the size whenever the tab becomes active so Leaflet recomputes the layout
// and requests the missing tiles.
function InvalidateOnActive({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const raf = requestAnimationFrame(() => map.invalidateSize());
    return () => cancelAnimationFrame(raf);
  }, [active, map]);
  return null;
}

InvalidateOnActive.propTypes = {
  active: PropTypes.bool.isRequired,
};

function InspectionsMapTab({ fetchParams, active, selectedDeviceType }) {
  const { t } = useTranslation();

  const [mapData, mapStats, mapLoading, mapError, fetchInspectionsMap] =
    useInspectionsStore(
      (state) => [
        state.mapData,
        state.mapStats,
        state.mapLoading,
        state.mapError,
        state.fetchInspectionsMap,
      ],
      shallow,
    );

  const nominatimAvailable = useNominatimStore((s) => s.available);

  const [usersByIds, usersAllIds] = useUserStore(
    (state) => [state.byIds, state.allIds],
    shallow,
  );

  // Inspection rows carry the ERP-side inspector number (e.g. "03"); map it
  // to the user's display name so the pin popup shows a human label instead
  // of a bare code. Built from the already-loaded user store — no extra
  // fetch. Falls back to the raw number when no matching user exists.
  const inspectorNameByNumber = useMemo(() => {
    const map = {};
    usersAllIds.forEach((id) => {
      const u = usersByIds[id];
      if (u?.number) map[u.number] = u.name;
    });
    return map;
  }, [usersByIds, usersAllIds]);

  const paramsKey = useMemo(() => makeMapKey(fetchParams), [fetchParams]);

  useEffect(() => {
    if (!active) return;
    fetchInspectionsMap(fetchParams);
    // Serialize params to a stable key so re-renders don't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, active]);

  const markers = useMemo(
    () => mapData.filter((item) => item.lat != null && item.lon != null),
    [mapData],
  );

  // Register any new device types into the module-level cache so each
  // keeps its assigned color for the whole session. Items without a
  // device type fall back to the "unknown" color.
  const { colorForType } = useMemo(() => {
    const unique = Array.from(
      new Set(markers.map((m) => m.device_type).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));

    unique.forEach((type) => {
      if (deviceColorCache.has(type)) return;
      const slot = deviceColorCache.size % DEVICE_COLOR_PALETTE.length;
      deviceColorCache.set(type, DEVICE_COLOR_PALETTE[slot]);
    });

    const map = {};
    unique.forEach((type) => {
      map[type] = deviceColorCache.get(type);
    });

    return { colorForType: map };
  }, [markers]);

  const getMarkerColor = (item) =>
    item.device_type ? colorForType[item.device_type] : DEVICE_COLOR_FALLBACK;

  const visibleMarkers = useMemo(() => {
    if (selectedDeviceType === null) return markers;
    if (selectedDeviceType === UNKNOWN_TYPE)
      return markers.filter((m) => !m.device_type);
    return markers.filter((m) => m.device_type === selectedDeviceType);
  }, [markers, selectedDeviceType]);

  const statsLine = t("inspections.map_stats", {
    located: mapStats.located,
    total: mapStats.total,
    unlocated: mapStats.unlocated,
  });

  return (
    <div>
      {mapError && (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("inspections.map_error")}
        </div>
      )}

      {nominatimAvailable === false && (
        <div className="alert alert-warning mb-3">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("nominatim.unavailable")}
        </div>
      )}

      <div className="inspections-map-wrapper">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          attributionControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <InvalidateOnActive active={active} />
          <AttributionControl prefix={false} />
          <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} />
          {visibleMarkers.map((item) => (
            <Marker
              key={item.slug}
              position={[item.lat, item.lon]}
              icon={getColoredIcon(getMarkerColor(item))}
            >
              <Popup>
                <div className="small">
                  {item.customer_nr && (
                    <div className="fw-bold">
                      {item.customer_nr}
                      {item.customer_name ? ` · ${item.customer_name}` : ""}
                    </div>
                  )}
                  {item.property_nr && (
                    <div>
                      {item.property_nr}
                      {item.property_name ? ` · ${item.property_name}` : ""}
                    </div>
                  )}
                  {item.property_address && <div>{item.property_address}</div>}
                  {item.next_inspection && (
                    <div className="text-muted">
                      {t("inspections.next_inspection")}: {item.next_inspection}
                    </div>
                  )}
                  {item.inspector && (
                    <div className="text-muted">
                      {t("inspections.inspector")}:{" "}
                      {inspectorNameByNumber[item.inspector]
                        ? `${inspectorNameByNumber[item.inspector]} (${item.inspector})`
                        : item.inspector}
                    </div>
                  )}
                  <div className="mt-2">
                    <Link to={`/sqlbase/inspections/${item.slug}/details`}>
                      {t("inspections.card")} &raquo;
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {mapLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: "rgba(255,255,255,0.5)", zIndex: 500 }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t("inspections.map_loading")}</span>
            </div>
          </div>
        )}
      </div>

      {!mapLoading && mapStats.total === 0 && (
        <div className="text-muted small mb-2">{t("inspections.map_empty")}</div>
      )}

      {mapStats.total > 0 && (
        <div className="text-muted small">{statsLine}</div>
      )}
    </div>
  );
}

InspectionsMapTab.propTypes = {
  fetchParams: PropTypes.shape({
    inspector: PropTypes.arrayOf(PropTypes.string),
    from: PropTypes.string,
    to: PropTypes.string,
    device_type: PropTypes.string,
    sort_by: PropTypes.string,
    sort_dir: PropTypes.string,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  selectedDeviceType: PropTypes.string,
};

InspectionsMapTab.defaultProps = {
  selectedDeviceType: null,
};

export default InspectionsMapTab;
